import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const script = [
  {
    id: 0,
    role: 'agent',
    name: 'J&J Demand Sensing Agent',
    time: 'Just now',
    text: 'Hello, anything I can help with?',
  },
  {
    id: 1,
    role: 'user',
    name: 'Sara Chen',
    time: '08/03/26, 8:09 AM',
    text: 'The FLR02 number is interesting. Can you show me consumption rate for the past 30 days vs. the prior 30? And what does the comparable account data look like?',
  },
  {
    id: 2,
    role: 'agent',
    name: 'J&J Demand Sensing Agent',
    time: '08/03/26, 8:06 AM',
    text: 'FLR02 is up 34% vs. prior 30 days — no matching procedure volume increase. Comparable accounts linked this pattern to a new attending or technique change. Recommend confirming with the OR nurse manager before adjusting par. Happy to draft a note to her.',
  },
  {
    id: 3,
    role: 'user',
    name: 'Sara Chen',
    time: '08/03/26, 8:09 AM',
    text: 'Draft a note to the OR nurse manager and create the two Trocar orders.',
  },
  {
    id: 4,
    role: 'agent',
    name: 'J&J Demand Sensing Agent',
    time: '08/03/26, 8:06 AM',
    text: 'Order requests initiated for D11LT (32 units) and TB11LT (11 units). Order numbers will appear in your activity feed once confirmed. Writing the draft note to OR nurse manager for you to review before sending.',
  },
]

export default function ChatPanel({ onClose, onNoteReady }) {
  const [shown, setShown] = useState([])
  const [cursor, setCursor] = useState(1)
  const [typing, setTyping] = useState(false)
  const timerRef = useRef(null)
  const bottomRef = useRef(null)

  const isDone = cursor >= script.length && !typing

  // Show greeting after panel slides in
  useEffect(() => {
    const t = setTimeout(() => setShown([script[0]]), 350)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [shown, typing])

  useEffect(() => () => clearTimeout(timerRef.current), [])

  const advance = () => {
    if (typing || cursor >= script.length) return
    const next = script[cursor]
    setCursor(c => c + 1)

    if (next.role === 'user') {
      setShown(prev => [...prev, next])
    } else {
      setTyping(true)
      timerRef.current = setTimeout(() => {
        setTyping(false)
        setShown(prev => [...prev, next])
        if (next.id === 4) {
          setTimeout(() => onNoteReady?.(), 700)
        }
      }, 1100)
    }
  }

  const replay = () => {
    clearTimeout(timerRef.current)
    setShown([])
    setTyping(false)
    setCursor(1)
    setTimeout(() => setShown([script[0]]), 200)
  }

  const canAdvance = !typing && cursor < script.length

  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, layout: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } }}
      style={{
        width: 'clamp(300px, 25%, 420px)',
        height: '100%',
        flexShrink: 0,
        background: 'rgba(0,0,0,0.3)',
        borderRadius: 4,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{
        padding: '18px 20px 14px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexShrink: 0,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <span style={{
          fontSize: 10, fontWeight: 700, color: '#A39992',
          textTransform: 'uppercase', letterSpacing: '0.09em',
          lineHeight: 1.4, flex: 1, paddingRight: 12,
        }}>
          J&J Demand Sensing Agent and Mercy General Inventory Agent Overnight
        </span>
        <motion.button
          whileHover={{ scale: 1.15, background: 'rgba(255,255,255,0.12)' }}
          whileTap={{ scale: 0.9 }}
          onClick={onClose}
          style={{
            width: 24, height: 24, borderRadius: 6, flexShrink: 0,
            background: 'transparent', border: 'none',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#A39992', fontSize: 14, fontFamily: 'inherit',
          }}
        >
          ✕
        </motion.button>
      </div>

      {/* Messages — click to advance */}
      <div
        onClick={advance}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 20px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 22,
          cursor: canAdvance ? 'pointer' : 'default',
          userSelect: 'none',
        }}
        className="feed-scroll"
      >
        <AnimatePresence>
          {shown.map(msg => (
            <Message key={msg.id} msg={msg} />
          ))}
          {typing && <TypingIndicator key="typing" />}
        </AnimatePresence>

        <div ref={bottomRef} />
      </div>

      {/* Bottom bar — hint or replay */}
      <div style={{
        padding: '10px 20px 14px',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: isDone ? 'center' : 'flex-start',
        minHeight: 44,
      }}>
        <AnimatePresence mode="wait">
          {isDone ? (
            <motion.button
              key="replay"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={replay}
              whileHover={{ scale: 1.04, background: 'rgba(255,255,255,0.1)' }}
              whileTap={{ scale: 0.97 }}
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 8,
                padding: '6px 16px',
                color: '#A39992',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                letterSpacing: '0.04em',
              }}
            >
              ↺ Replay conversation
            </motion.button>
          ) : canAdvance ? (
            <div key="spacer" />
          ) : (
            <div key="wait" />
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  )
}

function Message({ msg }) {
  const isUser = msg.role === 'user'
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        gap: 5,
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 6,
        flexDirection: isUser ? 'row-reverse' : 'row',
      }}>
        <span style={{
          fontSize: 10, fontWeight: 700,
          color: isUser ? '#F8F8F7' : '#A39992',
          textTransform: 'uppercase', letterSpacing: '0.08em',
        }}>
          {msg.name}
        </span>
        <span style={{ fontSize: 10, color: '#A39992' }}>{msg.time}</span>
      </div>
      <p style={{
        fontSize: 12,
        color: isUser ? '#D5CFC9' : '#FFFFFF',
        lineHeight: 1.65,
        maxWidth: '92%',
        textAlign: isUser ? 'right' : 'left',
        margin: 0,
      }}>
        {msg.text}
      </p>
    </motion.div>
  )
}

function TypingIndicator() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.3 }}
      style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
    >
      <span style={{
        fontSize: 10, fontWeight: 700, color: '#A39992',
        textTransform: 'uppercase', letterSpacing: '0.08em',
      }}>
        J&J Demand Sensing Agent
      </span>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center', height: 18 }}>
        {[0, 1, 2].map(i => (
          <motion.div
            key={i}
            animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }}
            transition={{ duration: 0.75, repeat: Infinity, delay: i * 0.17, ease: 'easeInOut' }}
            style={{ width: 5, height: 5, borderRadius: '50%', background: '#A39992' }}
          />
        ))}
      </div>
    </motion.div>
  )
}
